import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ProfileSection = ({ teacher, onUpdate }) => {
  const [fullName, setFullName] = useState(teacher?.full_name || '');
  const [email, setEmail] = useState(teacher?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const handleSaveProfile = async () => {
    if (!fullName?.trim()) {
      setProfileMsg({ type: 'error', text: 'Display name is required.' });
      return;
    }
    setSaving(true);
    setProfileMsg(null);
    try {
      const { error: authError } = await supabase?.auth?.updateUser({ email });
      if (authError) throw authError;
      const { error: dbError } = await supabase
        ?.from('teachers')
        ?.update({ full_name: fullName?.trim(), email: email?.trim(), updated_at: new Date()?.toISOString() })
        ?.eq('id', teacher?.id);
      if (dbError) throw dbError;
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      onUpdate?.({ full_name: fullName?.trim(), email: email?.trim() });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err?.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (newPassword?.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setSavingPassword(true);
    setPasswordMsg(null);
    try {
      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: teacher?.email,
        password: currentPassword,
      });
      if (signInError) throw new Error('Current password is incorrect.');
      const { error } = await supabase?.auth?.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err?.message || 'Failed to change password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="User" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Teacher Profile</h2>
          <p className="text-sm text-muted-foreground">Update your display name, email address and password</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Display Name</label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e?.target?.value)}
            placeholder="Your full name"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e?.target?.value)}
            placeholder="your@email.com"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
        {profileMsg && (
          <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
            profileMsg?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
          }`}>
            <Icon name={profileMsg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
            {profileMsg?.text}
          </div>
        )}
        <button
          onClick={handleSaveProfile}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Save" size={16} />}
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Change Password</h3>
        <div className="space-y-4">
          {[{ label: 'Current Password', value: currentPassword, setter: setCurrentPassword, show: showCurrentPw, toggle: setShowCurrentPw },
            { label: 'New Password', value: newPassword, setter: setNewPassword, show: showNewPw, toggle: setShowNewPw },
            { label: 'Confirm New Password', value: confirmPassword, setter: setConfirmPassword, show: showConfirmPw, toggle: setShowConfirmPw }
          ]?.map(({ label, value, setter, show, toggle }) => (
            <div key={label}>
              <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={value}
                  onChange={e => setter(e?.target?.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => toggle(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={show ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
            </div>
          ))}
          {passwordMsg && (
            <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
              passwordMsg?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
            }`}>
              <Icon name={passwordMsg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
              {passwordMsg?.text}
            </div>
          )}
          <button
            onClick={handleChangePassword}
            disabled={savingPassword}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
          >
            {savingPassword ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Lock" size={16} />}
            {savingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;
